import {
  useLazyFullSearchQuery,
  useLazySearchAccessoriesQuery,
  useLazySearchArticlesQuery,
  useLazySearchDocumentsQuery,
  useLazySearchProductsQuery,
} from '@api/search.api';
import { LoadingStatus, SearchContentSection, SearchRequest } from '@api/types';
import { FullSearchResults } from '@components';
import { BlockContainer } from '@components/blocks';
import { useAppDispatch, useAppSelector } from '@hooks';
import { Box, styled } from '@mui/material';
import { setCategoryInitialWorking } from '@store/catalog/catalog.slice';
import {
  getProductsCategoryLink,
  getSearchOffset,
  getSearchOffsets,
  getSearchQuery,
  getSearchResults,
  getSearchSection,
  setFullSearch,
} from '@store/search/search.slice';
import { useEffect } from 'react';

// STYLES
//--------
const SearchBlockWrapper = styled(Box)`
  // Insert your styles here
`;

// TYPES
//-------
export type SearchBlockProps = {
  itemsPerPage?: number;
};

// COMPONENTS
//------------
export const SearchBlock = ({ itemsPerPage = 12 }: SearchBlockProps) => {
  // Connection to Redux Store
  const dispatch = useAppDispatch();
  const currentQuery = useAppSelector(getSearchQuery);
  const currentSection = useAppSelector(getSearchSection);
  const currentOffset = useAppSelector(getSearchOffset);
  const currentOffsets = useAppSelector(getSearchOffsets);
  const productsCategoryLink = useAppSelector(getProductsCategoryLink);
  const results = useAppSelector(getSearchResults);

  // Search API call
  const [triggerFullSearch, response] = useLazyFullSearchQuery();
  const [triggerArticlesSearch, articlesStatus] = useLazySearchArticlesQuery();
  const [triggerDocumentsSearch, documentsStatus] = useLazySearchDocumentsQuery();
  const [triggerProductsSearch, productsStatus] = useLazySearchProductsQuery();
  const [triggerAccessoriesSearch, accessoriesStatus] = useLazySearchAccessoriesQuery();

  const statuses: { [Key in SearchContentSection]: LoadingStatus } = {
    articles: articlesStatus,
    documents: documentsStatus,
    products: productsStatus,
    accessories: accessoriesStatus,
  };

  // FULL SEARCH
  const fetchSearchResults = () => {
    if (currentQuery && currentQuery.length > 2) {
      dispatch(setCategoryInitialWorking(true));
      triggerFullSearch({ limit: itemsPerPage, query: currentQuery }).then(() => {
        dispatch(setCategoryInitialWorking(false));
      });
    }
  };

  // PARTIAL SEARCHES - pagination
  useEffect(() => {
    if (currentQuery && currentQuery.length > 2) {
      const request: SearchRequest = {
        limit: itemsPerPage,
        offset: currentOffset,
        query: currentQuery,
      };
      switch (currentSection) {
        case 'accessories':
          triggerAccessoriesSearch(request);
          break;
        case 'articles':
          triggerArticlesSearch(request);
          break;
        case 'documents':
          triggerDocumentsSearch(request);
          break;
        case 'products':
          triggerProductsSearch({ ...request, categoryLink: productsCategoryLink });
      }
    }
  }, [currentOffsets, productsCategoryLink]);

  // Watch current query in store and fetch fresh API data if it changes
  useEffect(() => {
    fetchSearchResults();
  }, [currentQuery]);

  // Set Full Search Mode
  // When this block is on page, Quick Search will not be shown
  useEffect(() => {
    dispatch(setFullSearch(true));
    return () => {
      dispatch(setFullSearch(false));
    };
  }, []);

  return (
    <BlockContainer>
      <SearchBlockWrapper className='search-block'>
        <FullSearchResults
          results={results}
          statuses={statuses}
          itemsPerPage={itemsPerPage}
          {...response}
        />
      </SearchBlockWrapper>
    </BlockContainer>
  );
};
